import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { recurringApi, type RecurringRunLogEntry } from '../../api/recurring_api';

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  created: 'success',
  skipped: 'warning',
  no_credits: 'error',
  inactive: 'default',
  error: 'error',
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
}

export function RecurringRunLogPage() {
  const [entries, setEntries] = useState<RecurringRunLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const loadEntries = async (loadCursor?: string) => {
    setLoading(true);
    try {
      const response = await recurringApi.runLog(loadCursor);
      setEntries(prev => loadCursor ? [...prev, ...response.items] : response.items);
      setCursor(response.cursor || null);
      setHasMore(!!response.cursor);
    } catch (err) {
      console.error('Failed to load run log:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, []);

  const createdCount = entries.filter(e => e.status === 'created').length;
  const skippedCount = entries.filter(e => e.status === 'skipped').length;
  const failedCount = entries.filter(e => e.status === 'no_credits' || e.status === 'error').length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button
          component={RouterLink}
          to="/manager"
          startIcon={<ArrowBackIcon />}
        >
          Manager
        </Button>
        <Typography variant="h4">Recurring Cron Log</Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Counts reflect entries loaded so far — load more to see further history.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" color="success.main">{createdCount}</Typography>
            <Typography variant="body2" color="text.secondary">Created (loaded)</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" color="warning.main">{skippedCount}</Typography>
            <Typography variant="body2" color="text.secondary">Skipped (loaded)</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" color="error.main">{failedCount}</Typography>
            <Typography variant="body2" color="text.secondary">Failed (loaded)</Typography>
          </CardContent>
        </Card>
      </Box>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Request</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(entry => (
                <TableRow key={entry.uuid}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(entry.date_created)}
                  </TableCell>
                  <TableCell>
                    {entry.recurring_title || entry.recurring_uuid}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.status}
                      size="small"
                      color={STATUS_COLORS[entry.status] || 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {entry.request_key ? (
                      <Link
                        component={RouterLink}
                        to={`/assistant/request/${entry.request_key}`}
                      >
                        View Request
                      </Link>
                    ) : '-'}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {entry.message || '-'}
                  </TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No recurring cron runs yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {hasMore && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Button
              variant="outlined"
              onClick={() => loadEntries(cursor || undefined)}
            >
              Load More
            </Button>
          </Box>
        )}
      </Card>
    </Container>
  );
}
